import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { complianceRulesService } from '../../../services/supabaseService';

const ComplianceRulesSection = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    condition: '',
    action: '',
    enabled: true,
    priority: 2
  });

  // Load compliance rules from Supabase on component mount
  useEffect(() => {
    loadRules();
  }, []);

  const loadRules = async () => {
    try {
      setLoading(true);
      const data = await complianceRulesService?.getAll();
      setRules(data);
    } catch (error) {
      console.error('Error loading compliance rules:', error);
    } finally {
      setLoading(false);
    }
  };

  const conditionOptions = [
    { value: 'Same violation type within 30 days', label: 'Same violation type within 30 days' },
    { value: 'No attendance violations for 90 days', label: 'No attendance violations for 90 days' },
    { value: 'Violation severity = Critical', label: 'Violation severity = Critical' },
    { value: 'No violations for 60 days', label: 'No violations for 60 days' },
    { value: 'Total points exceed threshold', label: 'Total points exceed threshold' },
    { value: 'Multiple violations in single day', label: 'Multiple violations in single day' }
  ];

  const actionOptions = [
    { value: 'Multiply points by 1.5x', label: 'Multiply points by 1.5x' },
    { value: 'Reduce total points by 1.0', label: 'Reduce total points by 1.0' },
    { value: 'Send email to HR team', label: 'Send email to HR team' },
    { value: 'Reduce total points by 0.5', label: 'Reduce total points by 0.5' },
    { value: 'Trigger manager review', label: 'Trigger manager review' },
    { value: 'Create incident report', label: 'Create incident report' }
  ];

  const priorityOptions = [
    { value: 1, label: 'High Priority' },
    { value: 2, label: 'Medium Priority' },
    { value: 3, label: 'Low Priority' }
  ];

  const getPriorityBadge = (priority) => {
    const badges = {
      1: { label: 'High', color: 'bg-error/10 text-error' },
      2: { label: 'Medium', color: 'bg-warning/10 text-warning' },
      3: { label: 'Low', color: 'bg-success/10 text-success' }
    };
    return badges?.[priority] || badges?.[2];
  };

  const handleToggleEnabled = async (id) => {
    try {
      const rule = rules?.find(r => r?.id === id);
      if (rule) {
        const updatedRule = await complianceRulesService?.update(id, { enabled: !rule?.enabled });
        if (updatedRule) {
          setRules(prev => prev?.map(r => r?.id === id ? updatedRule : r));
        }
      }
    } catch (error) {
      console.error('Error toggling rule enabled:', error);
    }
  };

  const handleEdit = (rule) => {
    setEditingId(rule?.id);
    setFormData({
      name: rule?.name,
      description: rule?.description,
      condition: rule?.condition,
      action: rule?.action,
      enabled: rule?.enabled,
      priority: rule?.priority
    });
  };

  const handleSave = async () => {
    try {
      if (editingId) {
        const updatedRule = await complianceRulesService?.update(editingId, formData);
        if (updatedRule) {
          setRules(prev => prev?.map(r => r?.id === editingId ? updatedRule : r));
          setEditingId(null);
        }
      } else {
        const newRule = await complianceRulesService?.create(formData);
        if (newRule) {
          setRules(prev => [...prev, newRule]);
          setShowAddForm(false);
        }
      }
      setFormData({ name: '', description: '', condition: '', action: '', enabled: true, priority: 2 });
    } catch (error) {
      console.error('Error saving compliance rule:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      const success = await complianceRulesService?.delete(id);
      if (success) {
        setRules(prev => prev?.filter(r => r?.id !== id));
      }
    } catch (error) {
      console.error('Error deleting compliance rule:', error);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setShowAddForm(false);
    setFormData({ name: '', description: '', condition: '', action: '', enabled: true, priority: 2 });
  };

  return (
    <div className="p-6">
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-sm text-muted-foreground">Loading compliance rules...</p>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-heading font-semibold text-xl text-foreground mb-1">
                Compliance Rules
              </h2>
              <p className="font-body text-sm text-muted-foreground">
                Create conditional logic, automated workflows, and custom compliance calculations
              </p>
            </div>
            <Button
              variant="default"
              iconName="Plus"
              iconPosition="left"
              onClick={() => setShowAddForm(true)}
            >
              Add Rule
            </Button>
          </div>

          {showAddForm && (
            <div className="bg-muted/50 rounded-lg p-4 mb-6">
              <h3 className="font-heading font-semibold text-base text-foreground mb-4">
                Create New Rule
              </h3>
              <div className="space-y-4">
                <Input
                  label="Rule Name"
                  value={formData?.name}
                  onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                  placeholder="Enter rule name"
                />
                <Input
                  label="Description"
                  value={formData?.description}
                  onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
                  placeholder="Describe what this rule does"
                />
                <Select
                  label="Condition (When)"
                  options={conditionOptions}
                  value={formData?.condition}
                  onChange={(value) => setFormData({ ...formData, condition: value })}
                  placeholder="Select trigger condition"
                />
                <Select
                  label="Action (Then)"
                  options={actionOptions}
                  value={formData?.action}
                  onChange={(value) => setFormData({ ...formData, action: value })}
                  placeholder="Select action to perform"
                />
                <Select
                  label="Priority"
                  options={priorityOptions}
                  value={formData?.priority}
                  onChange={(value) => setFormData({ ...formData, priority: value })}
                />
                <Checkbox
                  label="Enable this rule immediately"
                  checked={formData?.enabled}
                  onChange={(e) => setFormData({ ...formData, enabled: e?.target?.checked })}
                />
              </div>
              <div className="flex justify-end gap-3 mt-4">
                <Button variant="outline" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button variant="default" onClick={handleSave}>
                  Create Rule
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {rules?.sort((a, b) => a?.priority - b?.priority)?.map((rule) => (
              <div
                key={rule?.id}
                className={`rounded-lg border p-4 transition-smooth ${
                  rule?.enabled ? 'bg-card border-border' : 'bg-muted/30 border-muted opacity-60'
                }`}
              >
                {editingId === rule?.id ? (
                  <div>
                    <div className="space-y-4 mb-4">
                      <Input
                        label="Rule Name"
                        value={formData?.name}
                        onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                      />
                      <Input
                        label="Description"
                        value={formData?.description}
                        onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
                      />
                      <Select
                        label="Condition (When)"
                        options={conditionOptions}
                        value={formData?.condition}
                        onChange={(value) => setFormData({ ...formData, condition: value })}
                      />
                      <Select
                        label="Action (Then)"
                        options={actionOptions}
                        value={formData?.action}
                        onChange={(value) => setFormData({ ...formData, action: value })}
                      />
                      <Select
                        label="Priority"
                        options={priorityOptions}
                        value={formData?.priority}
                        onChange={(value) => setFormData({ ...formData, priority: value })}
                      />
                      <Checkbox
                        label="Enable this rule"
                        checked={formData?.enabled}
                        onChange={(e) => setFormData({ ...formData, enabled: e?.target?.checked })}
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={handleCancel}>
                        Cancel
                      </Button>
                      <Button variant="default" size="sm" onClick={handleSave}>
                        Save Changes
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-heading font-semibold text-base text-foreground">
                            {rule?.name}
                          </h4>
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getPriorityBadge(rule?.priority)?.color}`}>
                            {getPriorityBadge(rule?.priority)?.label}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">
                          {rule?.description}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={rule?.enabled}
                            onChange={() => handleToggleEnabled(rule?.id)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-ring rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary" />
                        </label>
                      </div>
                    </div>

                    <div className="bg-muted/50 rounded-lg p-3 mb-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Icon name="GitBranch" size={16} className="text-primary" />
                            <span className="text-xs font-semibold text-muted-foreground">WHEN</span>
                          </div>
                          <p className="text-sm text-foreground">{rule?.condition}</p>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Icon name="Zap" size={16} className="text-accent" />
                            <span className="text-xs font-semibold text-muted-foreground">THEN</span>
                          </div>
                          <p className="text-sm text-foreground">{rule?.action}</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Edit2"
                        onClick={() => handleEdit(rule)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Trash2"
                        onClick={() => handleDelete(rule?.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-6 bg-primary/5 border border-primary/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Lightbulb" size={20} className="text-primary mt-0.5" />
              <div>
                <h4 className="font-heading font-semibold text-sm text-foreground mb-1">
                  Rule Engine Tips
                </h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Rules are evaluated in priority order (High → Medium → Low)</li>
                  <li>• Multiple rules can be triggered by a single violation event</li>
                  <li>• Disabled rules are preserved but not executed</li>
                  <li>• Use conditions and actions to create sophisticated compliance workflows</li>
                </ul>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ComplianceRulesSection;